import { useRef, useState, useCallback } from "react";
import { getCameraIcon } from "./CameraIconPicker";
import { UnplacedCamerasSidebar } from "./UnplacedCamerasSidebar";
import { MapOverlayLayer } from "./MapOverlayLayer";
import { MapOverlayToolbar } from "./MapOverlayToolbar";
import {
  useUpdateCamera,
  useMapOverlays,
  useCreateMapOverlay,
  useUpdateMapOverlay,
  useDeleteMapOverlay,
  useUploadOverlayImage,
} from "@/hooks/useCameraPlans";
import { cn, formatCameraNumber } from "@/lib/utils";
import type { Camera, MapOverlay } from "@/types/camera-plans";

interface CameraPlanImageMapProps {
  imageUrl: string;
  cameras: Camera[];
  cameraPlanId: string;
  selectedCameraId: string | null;
  onSelectCamera: (id: string | null) => void;
  className?: string;
}

export function CameraPlanImageMap({
  imageUrl,
  cameras,
  cameraPlanId,
  selectedCameraId,
  onSelectCamera,
  className,
}: CameraPlanImageMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingCameraId, setDraggingCameraId] = useState<string | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(
    null,
  );

  const updateCamera = useUpdateCamera();
  const { data: overlays = [] } = useMapOverlays(cameraPlanId);
  const createOverlay = useCreateMapOverlay();
  const updateOverlay = useUpdateMapOverlay();
  const deleteOverlay = useDeleteMapOverlay();
  const uploadOverlayImage = useUploadOverlayImage();

  const placedCameras = cameras.filter(
    (c) => c.positionX != null && c.positionY != null,
  );
  const unplacedCameras = cameras.filter(
    (c) => c.positionX == null || c.positionY == null,
  );

  const getRelativePosition = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const container = containerRef.current;
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;
      return {
        x: Math.max(0, Math.min(1, x)),
        y: Math.max(0, Math.min(1, y)),
      };
    },
    [],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const cameraId = e.dataTransfer.getData("camera-id");
      if (!cameraId) return;

      const pos = getRelativePosition(e.clientX, e.clientY);
      if (!pos) return;

      updateCamera.mutate({
        id: cameraId,
        cameraPlanId,
        updates: { positionX: pos.x, positionY: pos.y },
      });
    },
    [cameraPlanId, getRelativePosition, updateCamera],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleMarkerMouseDown = (camera: Camera, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingCameraId(camera.id);
    setSelectedOverlayId(null);
    onSelectCamera(camera.id);

    const handleMouseMove = (me: MouseEvent) => {
      const pos = getRelativePosition(me.clientX, me.clientY);
      if (pos) setDragPos(pos);
    };

    const handleMouseUp = (me: MouseEvent) => {
      const pos = getRelativePosition(me.clientX, me.clientY);
      if (pos) {
        updateCamera.mutate({
          id: camera.id,
          cameraPlanId,
          updates: { positionX: pos.x, positionY: pos.y },
        });
      }
      setDraggingCameraId(null);
      setDragPos(null);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleContextMenu = (camera: Camera, e: React.MouseEvent) => {
    e.preventDefault();
    // Remove from map (unplace)
    updateCamera.mutate({
      id: camera.id,
      cameraPlanId,
      updates: { positionX: null, positionY: null },
    });
  };

  // Rotate camera with shift+scroll
  const handleMarkerWheel = (camera: Camera, e: React.WheelEvent) => {
    if (!e.shiftKey) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? 15 : -15;
    const newRotation = ((camera.rotation || 0) + delta + 360) % 360;
    updateCamera.mutate({
      id: camera.id,
      cameraPlanId,
      updates: { rotation: newRotation },
    });
  };

  const handleBackgroundClick = () => {
    onSelectCamera(null);
    setSelectedOverlayId(null);
  };

  return (
    <div className="space-y-2">
      <MapOverlayToolbar
        cameraPlanId={cameraPlanId}
        onAddShape={(type) => {
          createOverlay.mutate({
            cameraPlanID: cameraPlanId,
            overlayType: type,
            positionX: 0.5,
            positionY: 0.5,
            width: type === "line" ? 0.2 : 0.1,
            height: type === "line" ? 0 : 0.1,
            rotation: 0,
            color: "#6b7280",
            opacity: 1,
            strokeWidth: 2,
            fill: false,
            imageURL: null,
            fontSize: 14,
            label: type === "text" ? "Label" : null,
            points:
              type === "line"
                ? [
                    { x: 0.4, y: 0.5 },
                    { x: 0.6, y: 0.5 },
                  ]
                : null,
            zIndex: 0,
            sortOrder: overlays.length,
            locked: false,
          });
        }}
        onUploadImage={(file) =>
          uploadOverlayImage.mutate({ cameraPlanId, file })
        }
        selectedOverlay={
          overlays.find((o) => o.id === selectedOverlayId) ?? null
        }
        onUpdateOverlay={(updates) => {
          if (selectedOverlayId) {
            updateOverlay.mutate({
              id: selectedOverlayId,
              cameraPlanId,
              updates,
            });
          }
        }}
        onDeleteOverlay={() => {
          if (selectedOverlayId) {
            deleteOverlay.mutate({ id: selectedOverlayId, cameraPlanId });
            setSelectedOverlayId(null);
          }
        }}
      />

      <div
        ref={containerRef}
        className={cn(
          "relative rounded-lg overflow-hidden border bg-muted select-none",
          className,
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleBackgroundClick}
      >
        <img
          src={imageUrl}
          alt="Camera plan"
          className="w-full h-full object-contain pointer-events-none"
          draggable={false}
        />

        {/* Overlays layer (behind cameras) */}
        <MapOverlayLayer
          overlays={overlays}
          selectedOverlayId={selectedOverlayId}
          onSelectOverlay={(id) => {
            setSelectedOverlayId(id);
            onSelectCamera(null);
          }}
          onUpdateOverlay={(id, updates) =>
            updateOverlay.mutate({ id, cameraPlanId, updates })
          }
          getRelativePosition={getRelativePosition}
        />

        {/* Camera markers */}
        {placedCameras.map((camera) => {
          const Icon = getCameraIcon(camera.icon);
          const isDragging = draggingCameraId === camera.id;
          const posX = isDragging && dragPos ? dragPos.x : camera.positionX!;
          const posY = isDragging && dragPos ? dragPos.y : camera.positionY!;
          const isSelected = selectedCameraId === camera.id;
          const rotation = camera.rotation || 0;

          return (
            <div
              key={camera.id}
              className={cn(
                "absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing z-10",
                isDragging && "opacity-70",
              )}
              style={{
                left: `${posX * 100}%`,
                top: `${posY * 100}%`,
              }}
              onMouseDown={(e) => handleMarkerMouseDown(camera, e)}
              onContextMenu={(e) => handleContextMenu(camera, e)}
              onWheel={(e) => handleMarkerWheel(camera, e)}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOverlayId(null);
                onSelectCamera(
                  camera.id === selectedCameraId ? null : camera.id,
                );
              }}
            >
              {/* Direction cone */}
              {rotation !== 0 && (
                <div
                  className="absolute w-0 h-0 pointer-events-none"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: "center bottom",
                    bottom: "50%",
                    left: "50%",
                    marginLeft: "-12px",
                  }}
                >
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "12px solid transparent",
                      borderRight: "12px solid transparent",
                      borderBottom: `28px solid ${camera.color}`,
                      opacity: 0.35,
                    }}
                  />
                </div>
              )}

              <div
                className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full border-2 shadow-md transition-transform",
                  isSelected ? "scale-125 border-white" : "border-white/80",
                )}
                style={{ backgroundColor: camera.color }}
              >
                <Icon className="h-4 w-4 text-white" />
              </div>
              <span className="mt-0.5 text-[10px] font-mono font-bold text-foreground bg-background/80 px-1 rounded">
                {formatCameraNumber(camera.number)}
              </span>
            </div>
          );
        })}

        {/* Tracks */}
        {placedCameras
          .filter((c) => c.track && c.track.features.length > 0)
          .map((camera) => (
            <CameraTrackOverlay key={`track-${camera.id}`} camera={camera} />
          ))}

        {/* Unplaced cameras sidebar */}
        <UnplacedCamerasSidebar
          cameras={unplacedCameras}
          selectedCameraId={selectedCameraId}
          onSelectCamera={onSelectCamera}
          onDragStart={() => {}}
        />

        {cameras.length === 0 && overlays.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50">
            <p className="text-muted-foreground text-sm">
              Add cameras and drag them onto the map
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function CameraTrackOverlay({ camera }: { camera: Camera }) {
  if (!camera.track) return null;

  const lineFeatures = camera.track.features.filter(
    (f) => f.geometry.type === "LineString",
  );

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 5 }}
    >
      {lineFeatures.map((feature, i) => {
        const coords = (feature.geometry as GeoJSON.LineString).coordinates;
        const points = coords
          .map(([x, y]) => `${x * 100}%,${y * 100}%`)
          .join(" ");
        return (
          <polyline
            key={i}
            points={points}
            fill="none"
            stroke={camera.color}
            strokeWidth="2"
            strokeDasharray="6 3"
            opacity="0.7"
          />
        );
      })}
    </svg>
  );
}
